import { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

interface ActionItemTextDialogProps {
  item?: ActionCenterItemV2;
  currentUserId?: number | null;
  open: boolean;
  onClose: () => void;
}

interface ItemTextDialogState {
  message: string;
  hideToDate: string | null;
  shouldShow: boolean;
}

/**
 * Renders a popup that displays contextual item text for an Action Center deep link.
 */
export function ActionItemTextDialog({
  item,
  currentUserId,
  open,
  onClose,
}: Readonly<ActionItemTextDialogProps>) {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<ItemTextDialogState>({
    message: '',
    hideToDate: null,
    shouldShow: false,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Close dialog when open becomes false
  useEffect(() => {
    if (!open) {
      setIsDialogOpen(false);
    }
  }, [open]);

  // Reset + fetch only when opening
  useEffect(() => {
    if (!open || !item || !currentUserId) return;

    const currentItem = item; // Capture for TypeScript
    const currentUserIdValue = currentUserId; // Capture for TypeScript

    const scope = (currentItem.scope ?? '').toString().trim();
    if (!currentItem.action_type || !scope) {
      return;
    }

    let canceled = false; // avoids race conditions if popup closes early

    async function fetchData() {
      setLoading(true);
      setState({
        message: '',
        hideToDate: null,
        shouldShow: false,
      });
      setIsDialogOpen(false);

      try {
        const subtype = (currentItem.subtype ?? '').toString().trim();
        const params = new URLSearchParams({
          type: currentItem.action_type,
          scope: scope,
        });
        if (subtype) {
          params.append('subtype', subtype);
        }

        const response: any = await fetchWrapper.get(
          `${urls.DevOps.getActionCenterItemText}?${params.toString()}`
        );

        if (!canceled) {
          const ownerText = response.messageOwner ?? '';
          const visibleText = response.messageVisibleTo ?? '';
          const itemIsOwner = Number(currentItem.responsible_id) === Number(currentUserIdValue);
          const selectedMessage = itemIsOwner
            ? (ownerText || visibleText)
            : (visibleText || ownerText);
          const hasMessage = Boolean((selectedMessage ?? '').toString().trim());
          const shouldShow = hasMessage && response.shouldShow;

          setState({
            message: selectedMessage ?? '',
            hideToDate: response.hideToDate ?? null,
            shouldShow: shouldShow,
          });
          setIsDialogOpen(shouldShow);
        }
      } catch (error) {
        if (!canceled) {
          console.error('Failed to fetch action item text', error);
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      canceled = true; // cleanup
    };
  }, [open, item, currentUserId]);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
    onClose();
  }, [onClose]);

  const handleHide = () => {
    if (!item) {
      return;
    }

    const scope = (item.scope ?? '').toString().trim();
    const subtype = (item.subtype ?? '').toString().trim();

    setLoading(true);

    fetchWrapper
      .post(urls.DevOps.hideActionCenterItemText, {
        type: item.action_type,
        scope: scope,
        subtype: subtype,
      })
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        console.error('Failed to hide action item text', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Only return null if we're not open and dialog is closed (prevents blinking during loading)
  if (!open && !isDialogOpen) return null;

  return (
    <Dialog 
      open={isDialogOpen} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableRestoreFocus>
      <DialogTitle>Important information</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {state.message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="small" disabled={loading}>
          Close
        </Button>
        <Button
          onClick={handleHide}
          variant="text"
          size="small"
          disabled={loading}
          sx={{ fontSize: '0.75rem' }}>
          Don&apos;t show for the next 30 days
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ActionItemTextDialog;


